import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useAuth } from '../../stores/authStore';

/** Time on a page before asking, so it never lands on someone mid-task the moment they arrive. */
export const FEEDBACK_ASK_DELAY_MS = 45_000;
const SNOOZE_DAYS = 14;
const TOPICS = ['Speed', 'Lead quality', 'Message copy', 'LinkedIn', 'Email', 'Setup', 'Price', 'Something broke'];

const snoozeKey = (userId: string) => `halr_feedback_snooze:${userId}`;

function snoozed(userId: string): boolean {
  try {
    return Number(localStorage.getItem(snoozeKey(userId)) || 0) > Date.now();
  } catch {
    return false;
  }
}

function snooze(userId: string, days = SNOOZE_DAYS) {
  try {
    localStorage.setItem(snoozeKey(userId), String(Date.now() + days * 86_400_000));
  } catch {
    /* private mode: it may ask again next visit */
  }
}

export function followUpQuestion(rating: number): string {
  if (rating <= 2) return 'What is getting in the way?';
  if (rating === 3) return 'What would make it better?';
  if (rating === 4) return 'What would make it a 5?';
  return 'What is working best for you?';
}

/** The in-app "How is Halr working for you?" card. Asks rarely, never blocks the page. */
export default function FeedbackPrompt() {
  const location = useLocation();
  const { user } = useAuth();
  const userId = (user as { id?: string } | null)?.id || '';
  const reduceMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [topics, setTopics] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const askedRef = useRef(false);
  const titleId = useId();
  const noteId = useId();

  useEffect(() => {
    if (!userId || askedRef.current || snoozed(userId)) return;
    const timer = window.setTimeout(async () => {
      if (askedRef.current || document.visibilityState !== 'visible') return;
      try {
        const res = await apiFetch('/api/feedback/prompt');
        const body = res.ok ? await res.json().catch(() => null) : null;
        if (body?.ask === true && !snoozed(userId)) {
          askedRef.current = true;
          setOpen(true);
        }
      } catch {
        /* no prompt this visit */
      }
    }, FEEDBACK_ASK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [userId, location.pathname]);

  const submit = async (withRating: number | null, note: string) => {
    if (sending) return false;
    setSending(true);
    setError('');
    try {
      const res = await apiFetch('/api/feedback', {
        method: 'POST',
        body: { rating: withRating, message: note.trim() || undefined, topics, source: 'prompt', route: location.pathname },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        setError(typeof data?.error === 'string' ? data.error : 'That did not send. Try again in a minute.');
        return false;
      }
      return true;
    } catch {
      setError('That did not send. Try again in a minute.');
      return false;
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    if (userId) snooze(userId);
    if (rating !== null && !sent) void submit(rating, message);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const send = async () => {
    if (rating === null) return;
    if (await submit(rating, message)) {
      setSent(true);
      if (userId) snooze(userId, 90);
      window.setTimeout(() => setOpen(false), 2500);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.section
          role="dialog"
          aria-labelledby={titleId}
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: 16 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-5 right-5 z-[70] w-[22rem] max-w-[calc(100vw-2.5rem)] rounded-xl border border-neutral-90/10 bg-white p-4 shadow-xl"
          data-testid="feedback-prompt"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 id={titleId} className="text-[14px] font-semibold text-neutral-90">
              {sent ? 'Thank you' : rating === null ? 'How is Halr working for you?' : followUpQuestion(rating)}
            </h2>
            <button
              type="button"
              onClick={close}
              className="-m-1.5 rounded-btn p-1.5 text-neutral-90/45 hover:bg-neutral-90/5 hover:text-neutral-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-90"
              aria-label="Close"
              data-testid="feedback-prompt-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {sent ? (
            <p className="mt-2 text-[13px] leading-relaxed text-neutral-90/65" role="status">
              The team reads every answer. If you wrote something we can fix, we will follow up by email.
            </p>
          ) : rating === null ? (
            <>
              <div className="mt-3 grid grid-cols-5 gap-1.5" role="group" aria-labelledby={titleId}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-label={`${n} out of 5`}
                    onClick={() => setRating(n)}
                    className="h-10 rounded-btn border border-neutral-90/15 text-[14px] font-semibold text-neutral-90 transition-colors hover:border-neutral-90/40 hover:bg-neutral-90/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90"
                    data-testid={`feedback-rating-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="mt-1.5 flex justify-between text-[11px] text-neutral-90/50" aria-hidden>
                <span>Not well</span>
                <span>Very well</span>
              </div>
            </>
          ) : (
            <>
              <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="What is it about">
                {TOPICS.map((t) => {
                  const on = topics.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setTopics((cur) => (on ? cur.filter((x) => x !== t) : [...cur, t]))}
                      className={`rounded-btn px-2.5 py-1.5 text-[12px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90 ${
                        on ? 'bg-neutral-90 text-white' : 'border border-neutral-90/15 text-neutral-90/70 hover:border-neutral-90/35'
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <label htmlFor={noteId} className="sr-only">Your note</label>
              <textarea
                id={noteId}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                maxLength={4000}
                placeholder="Optional. A sentence is plenty."
                className="mt-3 w-full resize-none rounded-btn border border-neutral-90/15 px-3 py-2 text-[13px] text-neutral-90 placeholder:text-neutral-90/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90"
                data-testid="feedback-prompt-note"
              />
              {error && <p className="mt-2 text-[12px] text-red-700">{error}</p>}
              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setRating(null)}
                  className="text-[12px] font-medium text-neutral-90/55 hover:text-neutral-90"
                >
                  Change rating ({rating})
                </button>
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={sending}
                  className="rounded-btn bg-neutral-90 px-3.5 py-2 text-[13px] font-medium text-white hover:bg-neutral-90/85 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-90"
                  data-testid="feedback-prompt-send"
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </>
          )}
        </motion.section>
      )}
    </AnimatePresence>
  );
}
